import { FC, ReactNode, useMemo } from 'react';
import {
  Button,
  Checkbox,
  DateRangePicker,
  InfiniteScrollMultiSelect,
  Input,
  InputSelect,
  Label,
  MultiSelect,
  RadioGroup,
  RadioGroupItem,
} from '@features/ui';
import { TOption } from '@src/types';
import { DateValueType } from '@lib/utils';
import { ReportPeriod } from '@src/types/reports';

interface ReportFilteringSettingsProps {
  showPeriodFilter?: boolean;
  selectedPeriod?: string;
  onPeriodChange?: (value: string) => void;
  periodOptions?: TOption[];
  periodRange?: string;
  dateRangeValue?: DateValueType;
  onDateRangeChange?: (value: DateValueType) => void;
  dateRangeLabel?: ReactNode;
  showGroupByFilter?: boolean;
  selectedGroupBy?: string;
  onGroupByChange?: (value: string) => void;
  groupByOptions?: TOption[];
  isGroupByRequired?: boolean;
  showProductsFilter?: boolean;
  selectedProducts?: string[];
  onProductsChange?: (values: string[]) => void;
  productOptions?: TOption[];
  showUserGroupsFilter?: boolean;
  selectedUserGroups?: string[];
  onUserGroupsChange?: (values: string[]) => void;
  userGroupOptions?: TOption[];
  showPaymentSystemsFilter?: boolean;
  selectedPaymentSystems?: string[];
  onPaymentSystemsChange?: (values: string[]) => void;
  paymentSystemOptions?: TOption[];
  showProductCategoriesFilter?: boolean;
  selectedProductCategories?: string[];
  onProductCategoriesChange?: (values: string[]) => void;
  productCategoryOptions?: TOption[];
  showCategoriesFilter?: boolean;
  selectedCategories?: string[];
  onCategoriesChange?: (values: string[]) => void;
  categoryOptions?: TOption[];
  selectedFiles?: string[];
  onFilesChange?: (values: string[]) => void;
  fileOptions?: TOption[];
  showFilesFilter?: boolean;
  selectedSignupForms?: string[];
  onSignupFormsChange?: (values: string[]) => void;
  signupFormOptions?: TOption[];
  showSignupFormsFilter?: boolean;
  selectedInvoiceStatus?: string;
  onInvoiceStatusChange?: (value: string) => void;
  showInvoiceStatusFilter?: boolean;
  showCountriesFilter?: boolean;
  selectedCountries?: string[];
  onCountriesChange?: (values: string[]) => void;
  countryOptions?: TOption[];
  isCountriesMulti?: boolean;
  showCitiesFilter?: boolean;
  selectedCities?: string[];
  onCitiesChange?: (values: string[]) => void;
  cityOptions?: TOption[];
  cityOptionsLoading?: boolean;
  cityOptionsHasMore?: boolean;
  onCityOptionsLoadMore?: () => void;
  onCitySearchChange?: (value: string) => void;
  showCouponsFilter?: boolean;
  selectedCoupons?: string;
  onCouponsChange?: (value: string) => void;
  couponOptions?: TOption[];
  showFieldFilter?: boolean;
  selectedField?: string;
  onFieldChange?: (value: string) => void;
  fieldOptions?: TOption[];
  enableProductComparing?: boolean;
  selectedProductA?: string;
  onProductAChange?: (value: string) => void;
  selectedProductB?: string;
  onProductBChange?: (value: string) => void;
  productComparingOptions?: TOption[];
  showCompareToPreviousPeriod?: boolean;
  compareToPreviousPeriod?: boolean;
  onCompareToPreviousPeriodChange?: (checked: boolean) => void;
  showCompareToSamePeriodPreviousYear?: boolean;
  compareToSamePeriodPreviousYear?: boolean;
  onCompareToSamePeriodPreviousYearChange?: (checked: boolean) => void;
  showRollingDaysFilter?: boolean;
  selectedRollingDays?: string;
  onRollingDaysChange?: (value: string) => void;
  isLoading?: boolean;
  showRunReportButton?: boolean;
  onRunReport?: () => void;
  children?: ReactNode;
}

export const ReportFilteringSettings: FC<ReportFilteringSettingsProps> = ({
  showPeriodFilter = true,
  selectedPeriod = '',
  onPeriodChange,
  periodOptions = [],
  periodRange,
  dateRangeValue,
  onDateRangeChange,
  dateRangeLabel,
  showGroupByFilter = true,
  selectedGroupBy = '',
  onGroupByChange,
  groupByOptions = [],
  isGroupByRequired = true,
  showProductsFilter = false,
  selectedProducts = [],
  onProductsChange,
  productOptions = [],
  showUserGroupsFilter = false,
  selectedUserGroups = [],
  onUserGroupsChange,
  userGroupOptions = [],
  showPaymentSystemsFilter = false,
  selectedPaymentSystems = [],
  onPaymentSystemsChange,
  paymentSystemOptions = [],
  showProductCategoriesFilter = false,
  selectedProductCategories = [],
  onProductCategoriesChange,
  productCategoryOptions = [],
  showCategoriesFilter = false,
  selectedCategories = [],
  onCategoriesChange,
  categoryOptions = [],
  showFilesFilter = false,
  selectedFiles = [],
  onFilesChange,
  fileOptions = [],
  showSignupFormsFilter = false,
  selectedSignupForms = [],
  onSignupFormsChange,
  signupFormOptions = [],
  showInvoiceStatusFilter = false,
  selectedInvoiceStatus = '',
  onInvoiceStatusChange,
  showCountriesFilter = false,
  selectedCountries = [],
  onCountriesChange,
  countryOptions = [],
  isCountriesMulti = true,
  showCompareToPreviousPeriod = false,
  compareToPreviousPeriod = false,
  showCitiesFilter = false,
  selectedCities = [],
  onCitiesChange,
  cityOptions = [],
  cityOptionsLoading = false,
  cityOptionsHasMore = false,
  onCityOptionsLoadMore,
  onCitySearchChange,
  showCouponsFilter = false,
  selectedCoupons = '',
  onCouponsChange,
  couponOptions = [],
  showFieldFilter = false,
  selectedField = '',
  onFieldChange,
  fieldOptions = [],
  enableProductComparing = false,
  selectedProductA,
  onProductAChange,
  selectedProductB,
  onProductBChange,
  onCompareToPreviousPeriodChange,
  showCompareToSamePeriodPreviousYear = false,
  compareToSamePeriodPreviousYear = false,
  onCompareToSamePeriodPreviousYearChange,
  showRollingDaysFilter = false,
  selectedRollingDays = '30',
  onRollingDaysChange,
  isLoading = false,
  showRunReportButton = true,
  onRunReport,
  children,
}) => {
  const resolvedCityOptions = useMemo(() => {
    const selectedMissing = selectedCities
      .filter(
        (selectedCity) =>
          !cityOptions.some((option) => option.value === selectedCity)
      )
      .map((selectedCity) => ({ value: selectedCity, label: selectedCity }));

    return [...selectedMissing, ...cityOptions];
  }, [cityOptions, selectedCities]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        {/* Period Selection */}
        {showPeriodFilter && onPeriodChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="period-select"
            >
              Period
            </label>
            <div className="flex-1 flex items-center gap-2">
              <InputSelect
                className="max-w-[300px] text-white"
                id="period-select"
                options={periodOptions}
                placeholder="Select Period"
                value={selectedPeriod}
                onChange={onPeriodChange}
              />
              {periodRange && (
                <span className="text-gray-400 text-sm ml-2">
                  {periodRange}
                </span>
              )}
              {selectedPeriod === ReportPeriod.EXACT && onDateRangeChange && (
                <DateRangePicker
                  asSingle={false}
                  className="max-w-[300px]"
                  placeholder="Select Start - End Date"
                  size="sm"
                  value={dateRangeValue ?? null}
                  variant="outline"
                  onChange={onDateRangeChange}
                />
              )}
            </div>
          </div>
        )}

        {/* Custom Date Range (for non-period reports) */}
        {!showPeriodFilter && onDateRangeChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="users-date-range"
            >
              {dateRangeLabel || 'Date Range'}
            </label>
            <div className="flex-1">
              <DateRangePicker
                asSingle={false}
                className="max-w-[300px]"
                placeholder="Begin Date - End Date"
                size="sm"
                value={dateRangeValue ?? null}
                variant="outline"
                onChange={onDateRangeChange}
              />
            </div>
          </div>
        )}

        {/* Grouping Selection */}
        {showGroupByFilter && onGroupByChange && (
          <div className="flex items-center">
            <label
              className={`${isGroupByRequired ? 'text-red-500' : 'text-white'} min-w-[250px] text-sm`}
              htmlFor="group-by-select"
            >
              {isGroupByRequired ? '* ' : ''}Grouping
            </label>
            <div className="flex-1">
              <InputSelect
                className="max-w-[300px] text-white"
                id="group-by-select"
                options={groupByOptions}
                placeholder="-- Please Select --"
                value={selectedGroupBy}
                onChange={onGroupByChange}
              />
            </div>
          </div>
        )}

        {/* Products Filter */}
        {showProductsFilter && onProductsChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="product-select"
            >
              Products <br />
              <span className="text-gray-400">
                keep empty to include <br />
                all products to report
              </span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="product-select"
                options={productOptions}
                placeholder="-- Please Select --"
                value={selectedProducts}
                onChange={(values: (string | number)[] = []) =>
                  onProductsChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}

        {/* User Groups Filter */}
        {showUserGroupsFilter && onUserGroupsChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="user-group-select"
            >
              User Groups <br />
              <span className="text-gray-400">
                keep empty to include <br />
                all groups to report
              </span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="user-group-select"
                options={userGroupOptions}
                placeholder="-- Please Select --"
                value={selectedUserGroups}
                onChange={(values: (string | number)[] = []) =>
                  onUserGroupsChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}

        {/* Payment Systems Filter */}
        {showPaymentSystemsFilter && onPaymentSystemsChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="payment-system-select"
            >
              Payment Systems <br />
              <span className="text-gray-400">
                keep empty to include <br />
                all systems to report
              </span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="payment-system-select"
                options={paymentSystemOptions}
                placeholder="-- Please Select --"
                value={selectedPaymentSystems}
                onChange={(values: (string | number)[] = []) =>
                  onPaymentSystemsChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}

        {/* Product Categories Filter */}
        {showProductCategoriesFilter && onProductCategoriesChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="product-category-select"
            >
              Product Categories <br />
              <span className="text-gray-400">
                keep empty to include <br />
                all categories to report
              </span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="product-category-select"
                options={productCategoryOptions}
                placeholder="-- Please Select --"
                value={selectedProductCategories}
                onChange={(values: (string | number)[] = []) =>
                  onProductCategoriesChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}

        {/* Categories Filter */}
        {showCategoriesFilter && onCategoriesChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="category-select"
            >
              Categories <br />
              <span className="text-gray-400">
                Keep empty to report all categories
              </span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="category-select"
                options={categoryOptions}
                placeholder="-- Please Select --"
                value={selectedCategories}
                onChange={(values: (string | number)[] = []) =>
                  onCategoriesChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}
        {/* Files Filter */}
        {showFilesFilter && onFilesChange && (
          <div className="flex items-center">
            <label
              className="text-white min-w-[250px] text-sm"
              htmlFor="file-select"
            >
              Files <br />
              <span className="text-gray-400">keep empty to report all</span>
            </label>
            <div className="flex-1 max-w-[300px]">
              <MultiSelect
                id="file-select"
                options={fileOptions}
                placeholder="-- Please Select --"
                value={selectedFiles}
                onChange={(values: (string | number)[] = []) =>
                  onFilesChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            </div>
          </div>
        )}
      </div>
      {/* Signup Forms Filter */}
      {showSignupFormsFilter && onSignupFormsChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="signup-form-select"
          >
            Signup Forms <br />
            <span className="text-gray-400">Keep empty to report all</span>
          </label>
          <div className="flex-1 max-w-[300px]">
            <MultiSelect
              id="signup-form-select"
              options={signupFormOptions}
              placeholder="-- Please Select --"
              value={selectedSignupForms}
              onChange={(values: (string | number)[] = []) =>
                onSignupFormsChange(
                  values.filter((v): v is string => typeof v === 'string')
                )
              }
            />
          </div>
        </div>
      )}
      {/* Countries Filter */}
      {showCountriesFilter && onCountriesChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="country-select"
          >
            Countries <br />
            <span className="text-gray-400">Keep empty to report all</span>
          </label>
          <div className="flex-1 max-w-[300px]">
            {isCountriesMulti ? (
              <MultiSelect
                id="country-select"
                options={countryOptions}
                placeholder="-- Please Select --"
                value={selectedCountries}
                onChange={(values: (string | number)[] = []) =>
                  onCountriesChange(
                    values.filter((v): v is string => typeof v === 'string')
                  )
                }
              />
            ) : (
              <InputSelect
                id="country-select"
                options={countryOptions}
                placeholder="-- Please Select --"
                value={selectedCountries[0] || ''}
                onChange={(value) => onCountriesChange(value ? [value] : [])}
              />
            )}
          </div>
        </div>
      )}
      {/* Cities Filter */}
      {showCitiesFilter && onCitiesChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="city-select"
          >
            Cities <br />
            <span className="text-gray-400">Keep empty to report all</span>
          </label>
          <div className="flex-1 max-w-[300px]">
            <InfiniteScrollMultiSelect
              id="city-select"
              options={resolvedCityOptions}
              isLoading={cityOptionsLoading}
              hasMore={cityOptionsHasMore}
              placeholder="-- Please Select --"
              value={selectedCities}
              onChange={(value) => {
                const values = Array.isArray(value)
                  ? value
                  : value == null
                    ? []
                    : [value];

                onCitiesChange(
                  values.filter((v): v is string => typeof v === 'string')
                );
              }}
              onInputChange={onCitySearchChange}
              onMenuScrollToBottom={onCityOptionsLoadMore}
            />
          </div>
        </div>
      )}
      {showCouponsFilter && onCouponsChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="coupon-select"
          >
            Coupons Batch <br />
            <span className="text-gray-400">Keep empty to report all</span>
          </label>
          <div className="flex-1 max-w-[300px]">
            <InfiniteScrollMultiSelect
              id="coupon-select"
              isMulti={false}
              options={couponOptions}
              placeholder="-- Please Select --"
              value={selectedCoupons}
              onChange={(value) => onCouponsChange(String(value))}
            />
          </div>
        </div>
      )}
      {children}
      {/* Field Filter */}
      {showFieldFilter && onFieldChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="field-select"
          >
            Field
          </label>
          <div className="flex-1 max-w-[300px]">
            <InputSelect
              id="field-select"
              options={fieldOptions}
              placeholder="-- Please Select --"
              value={selectedField}
              onChange={(value) => onFieldChange(value ? value : '')}
            />
          </div>
        </div>
      )}
      {/* Invoice Status Filter */}
      {showInvoiceStatusFilter && onInvoiceStatusChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="invoice-status-select"
          >
            Invoice Status
          </label>
          <div className="flex-1 max-w-[300px]">
            <div className="text-gray-400">
              <RadioGroup
                defaultValue="all"
                value={selectedInvoiceStatus}
                onValueChange={onInvoiceStatusChange}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem id="r1" value="all" />
                  <Label htmlFor="r1">All</Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem id="r2" value="pending" />
                  <Label htmlFor="r2">Pending</Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem id="r3" value="completed" />
                  <Label htmlFor="r3">Completed</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </div>
      )}

      {/* Product Comparing (for Product Conversion report) */}
      {enableProductComparing && onProductAChange && onProductBChange && (
        <>
          <div className="flex items-center">
            <Label
              className="text-white min-w-[250px] text-sm"
              htmlFor="product-a-select"
            >
              Product A
            </Label>
            <div className="flex-1">
              <InputSelect
                className="max-w-[300px] text-white"
                id="product-a-select"
                options={productOptions}
                placeholder="-- Please Select --"
                value={selectedProductA}
                onChange={onProductAChange}
              />
            </div>
          </div>
          <div className="flex items-center">
            <Label
              className="text-white min-w-[250px] text-sm"
              htmlFor="product-b-select"
            >
              Product B
            </Label>
            <div className="flex-1">
              <InputSelect
                className="max-w-[300px] text-white"
                id="product-b-select"
                options={productOptions}
                placeholder="-- Please Select --"
                value={selectedProductB}
                onChange={onProductBChange}
              />
            </div>
          </div>
        </>
      )}

      {/* Rolling Days Filter */}
      {showRollingDaysFilter && onRollingDaysChange && (
        <div className="flex items-center">
          <label
            className="text-white min-w-[250px] text-sm"
            htmlFor="rolling-days-input"
          >
            Rolling Days
          </label>
          <div className="flex-1 max-w-[300px]">
            <Input
              id="rolling-days-input"
              placeholder="30"
              value={selectedRollingDays}
              variant="secondary"
              onChange={(e) => {
                onRollingDaysChange(e.target.value);
              }}
            />
          </div>
        </div>
      )}

      {/* Comparison Options */}
      {(showCompareToPreviousPeriod || showCompareToSamePeriodPreviousYear) && (
        <div className="flex flex-col gap-2 mt-2">
          {showCompareToPreviousPeriod && onCompareToPreviousPeriodChange && (
            <div className="flex items-center">
              <div className="min-w-[250px]" />
              <Checkbox
                checked={compareToPreviousPeriod}
                label="Compare to Previous Period"
                onCheckedChange={(checked) =>
                  onCompareToPreviousPeriodChange(checked as boolean)
                }
              />
            </div>
          )}
          {showCompareToSamePeriodPreviousYear &&
            onCompareToSamePeriodPreviousYearChange && (
              <div className="flex items-center">
                <div className="min-w-[250px]" />
                <Checkbox
                  checked={compareToSamePeriodPreviousYear}
                  label="Compare to Same Period in Previous Year"
                  onCheckedChange={(checked) =>
                    onCompareToSamePeriodPreviousYearChange(checked as boolean)
                  }
                />
              </div>
            )}
        </div>
      )}

      {/* Run Report Button */}
      {showRunReportButton && onRunReport && (
        <div className="flex items-center">
          <div className="min-w-[250px]" />
          <Button
            className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
            disabled={isLoading}
            onClick={onRunReport}
          >
            {isLoading ? 'Running...' : 'Run Report'}
          </Button>
        </div>
      )}
    </div>
  );
};
